import { isValidElement } from "react";

/**
 * Find leafs that we don't want to touch. This includes
 * - Arrays
 * - Functions
 * - Strings
 * - Numbers
 * - ReactElements
 * - Objects created by custom classes
 */
function isLeaf(a: any): boolean {
  return (
    Array.isArray(a) ||
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    isValidElement(a) ||
    typeof a === "function" ||
    typeof a !== "object" ||
    a === null ||
    a === undefined ||
    ("constructor" in a &&
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      a.constructor !== Object.prototype.constructor)
  );
}

/**
 * A node is a candidate for recursively merging props with defaults.
 */
function isNode(a: any): boolean {
  return !isLeaf(a);
}

/**
 * Detects recursively whether `b` has nested keys that `a` doesn't include.
 */
function nodeHasNewKeys(a: any, b: any) {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
  const aKeys = Object.keys(a);
  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
  const bKeys = Object.keys(b);

  for (const key of bKeys) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (!aKeys.includes(key) || a[key] === undefined) {
      return true;
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (isNode(b[key]) && isNode(a[key])) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (nodeHasNewKeys(a[key], b[key])) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Merge props while preserving object identities (objects, arrays, functions, etc).
 * This also ensures that shallow comparing props continue to work.
 * The merge strategy also handles React elements, classes, etc.
 */
export function mergeProps<
  PropsA extends Record<string, any>,
  PropsB extends Record<string, any>,
>(propsA: PropsA, propsB: PropsB): PropsB & PropsA {
  const result: Record<string, any> = {
    ...propsB,
  };
  Object.keys(propsA).forEach((key) => {
    if (!(key in result) || result[key] === undefined) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      result[key] = propsA[key];
      return;
    }
    if (isLeaf(propsB[key]) || isLeaf(propsA[key])) {
      return;
    }

    if (!nodeHasNewKeys(propsB[key], propsA[key])) {
      // No new keys in defaults, so we can ignore this.
      return;
    }

    // Deep merge object.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    result[key] = mergeProps(propsA[key], result[key]);
  });

  return result as PropsB & PropsA;
}
